<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { trainingStatusColor, trainingStatusLabel } from '../utils'
import type { ItemResult, KnowledgeTest, ProgramVersion, TrainingAssignment, TrainingCampaign, TrainingProgram } from '../types'
import { trainingApi } from '../services/trainingApi'
import TrainingDialog from './TrainingDialog.vue'
import TrainingRegistry from './TrainingRegistry.vue'
import { useStaffStore } from '@/modules/staff/stores/staffStore'
import { DocumentsApi } from '@/modules/documents-new/services/documentsApi'
import type { DocumentItem, DocumentVersion } from '@/modules/documents-new/types/document.types'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import { getApiError } from '@/composables/getApiError'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import type { IEmployeeTreeDepartment, IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'

const staff = useStaffStore()
const apiStore = useApiStore()
const employeeSelector = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)
const route = useRoute()
const router = useRouter()
const capability = ref({ manage: false, reports: false, employee: '' })
const capabilityLoaded = ref(false)
const revision = ref(0)
const selectedCampaign = ref<TrainingCampaign | null>(null)
const editingItem = ref<string | null>(null)
const activeAttempt = ref<string | null>(null)
const testFeedback = ref('')
const programEditing = ref(false)
const tab = ref(typeof route.query.tab === 'string' ? route.query.tab : 'mine')
const registryKind = computed(() => tab.value === 'programs' ? 'programs' : tab.value === 'campaigns' ? 'campaigns' : 'assignments')
async function switchTab(value: unknown) {
  await router.replace({ query: { tab: String(value) } })
  tab.value = String(value)
}
const loading = ref(false)
const saving = ref(false)
const error = ref('')
const formError = ref('')
const programs = ref<TrainingProgram[]>([])
const selectedAssignment = ref<TrainingAssignment | null>(null)
const programDialog = ref(false)
const campaignDialog = ref(false)
const itemDialog = ref(false)
const selectedProgram = ref<TrainingProgram | null>(null)
const selectedVersion = ref<ProgramVersion | null>(null)
const documents = ref<Pick<DocumentItem, 'id' | 'name'>[]>([])
const documentLoading = ref(false)
const documentVersions = ref<DocumentVersion[]>([])
const tests = ref<KnowledgeTest[]>([])
const testDialog = ref(false)
const takeTestDialog = ref(false)
const activeTestResult = ref<ItemResult | null>(null)
const testAnswers = ref<Record<string, string[]>>({})

const programForm = reactive({ name: '', code: '', category: '', description: '' })
const versionForm = reactive({ duration_minutes: 20, passing_percent: 80, validity_days: null as number | null })
const itemForm = reactive({ item_type: 'text', title: '', description: '', document_id: null as string | null, document_version: null as number | null, test_version: null as string | null, content: '', external_url: '' })
const testForm = reactive({ name: '', code: '', passing_percent: 80, time_limit_minutes: 20, max_attempts: 3, questions: [{ text: '', optionsText: '', correctIndex: 0 }] })
const campaignForm = reactive({
  name: '',
  program_version: '',
  starts_at: '',
  due_at: '',
  source_type: 'manual',
  source_id: '',
  target_departments: [] as string[],
  target_positions: [] as string[],
  target_employees: [] as string[],
})

const publishedVersions = computed(() => programs.value.flatMap(program => program.versions
  .filter(version => version.status === 'published')
  .map(version => ({ title: `${program.name} · версия ${version.version}`, value: version.id }))))
const testVersionItems = computed(() => tests.value.flatMap(test => test.versions
  .filter(version => version.status === 'published')
  .map(version => ({ title: `${test.name} · версия ${version.version}`, value: version.id }))))
const departmentItems = computed(() => {
  const result: Array<{ title: string; value: string }> = []
  const walk = (items: typeof staff.departments, level = 0) => items.forEach(item => {
    result.push({ title: `${'— '.repeat(level)}${item.short_name || item.name}`, value: item.id })
    if (item.children?.length) walk(item.children, level + 1)
  })
  walk(staff.departments)
  return result
})
const selectedEmployees = computed(() => {
  const selected = new Set(campaignForm.target_employees)
  const collect = (departments: IEmployeeTreeDepartment[]): IEmployeeTreeItem[] => departments.flatMap(department => [
    ...(department.employees || []).filter(employee => selected.has(employee.id)),
    ...collect(department.children || []),
  ])
  return collect(apiStore.employees)
})
const itemFormValid = computed(() => Boolean(itemForm.title.trim()) && (
  itemForm.item_type === 'text'
    ? Boolean(itemForm.content.trim())
    : ['document', 'acknowledgement'].includes(itemForm.item_type)
        ? Boolean(itemForm.document_version)
        : itemForm.item_type === 'test'
          ? Boolean(itemForm.test_version)
          : Boolean(itemForm.external_url.trim())
))
const testFormValid = computed(() => Boolean(testForm.name.trim() && testForm.code.trim())
  && testForm.questions.length > 0
  && testForm.questions.every(question => {
    const options = question.optionsText.split('\n').map(value => value.trim()).filter(Boolean)
    return Boolean(question.text.trim()) && options.length >= 2
      && question.correctIndex >= 0 && question.correctIndex < options.length
  }))
const campaignFormValid = computed(() => Boolean(
  campaignForm.name.trim()
  && campaignForm.program_version
  && campaignForm.starts_at
  && campaignForm.due_at
  && new Date(campaignForm.due_at) > new Date(campaignForm.starts_at)
  && (campaignForm.target_departments.length || campaignForm.target_positions.length || campaignForm.target_employees.length),
))
const currentTestComplete = computed(() => Boolean(activeTestResult.value?.test?.questions.length)
  && activeTestResult.value!.test!.questions.every(question => testAnswers.value[question.id]?.length))

const statusLabel = trainingStatusLabel
const statusColor = trainingStatusColor
const formatDate = (value?: string | null) => value ? new Date(value).toLocaleDateString('ru-RU') : '—'
const toIso = (value: string) => value ? new Date(value).toISOString() : ''
const toLocalInput = (value: Date) => {
  const offset = value.getTimezoneOffset() * 60_000
  return new Date(value.getTime() - offset).toISOString().slice(0, 16)
}
const errorMessage = (e: any, fallback: string) => getApiError(e,
  typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : fallback)

async function applyDeepLink() {
  const assignmentId = typeof route.query.assignment === 'string' ? route.query.assignment : ''
  const campaignId = typeof route.query.campaign === 'string' ? route.query.campaign : ''
  error.value = ''
  selectedAssignment.value = null
  try {
    if (assignmentId) selectedAssignment.value = await trainingApi.getAssignment(assignmentId)
    else selectedAssignment.value = null
    if (campaignId && !assignmentId) {
      selectedCampaign.value = await trainingApi.getCampaign(campaignId)
      fillCampaign(selectedCampaign.value)
      campaignDialog.value = true
    }
  } catch (e: any) {
    error.value = [403, 404].includes(e?.response?.status)
      ? 'Карточка недоступна или не найдена.'
      : errorMessage(e, 'Не удалось загрузить карточку. Повторите попытку.')
  }
}
async function openAssignment(row: TrainingAssignment) {
  await router.push({ query: { ...route.query, assignment: row.id } })
}
function closeAssignment(open: boolean) {
  if (open) return
  selectedAssignment.value = null
  const query = { ...route.query }
  delete query.assignment
  router.replace({ query })
}
async function load() {
  loading.value = true
  error.value = ''
  try {
    if (capability.value.manage) {
      const [programRows, testRows] = await Promise.all([trainingApi.getPrograms(), trainingApi.getTests()])
      programs.value = programRows
      tests.value = testRows
    }
    revision.value += 1
  } catch (e: any) {
    error.value = errorMessage(e, 'Не удалось обновить справочники обучения.')
  } finally {
    loading.value = false
  }
}
watch(() => route.query.assignment, applyDeepLink)
watch(() => route.query.campaign, applyDeepLink)

function openProgram(value?: unknown) {
  const program = value && typeof value === 'object' && 'versions' in value && Array.isArray(value.versions)
    ? value as TrainingProgram
    : null
  formError.value = ''
  selectedProgram.value = program
  selectedVersion.value = program?.versions[0] || null
  programEditing.value = !program
  Object.assign(programForm, program ? { name: program.name, code: program.code, category: program.category, description: program.description } : { name: '', code: '', category: '', description: '' })
  programDialog.value = true
}

function openNewProgram() {
  openProgram()
}

function closeProgram(open: boolean) {
  if (!open && programEditing.value && selectedProgram.value) {
    const { name, code, category, description } = selectedProgram.value
    Object.assign(programForm, { name, code, category, description })
    programEditing.value = false
    formError.value = ''
    return
  }
  programDialog.value = open
}

async function createProgram() {
  formError.value = ''
  if (!programForm.name.trim() || !programForm.code.trim() || saving.value) return
  saving.value = true
  try {
    if (selectedProgram.value) selectedProgram.value = await trainingApi.updateProgram(selectedProgram.value.id, programForm)
    else {
      selectedProgram.value = await trainingApi.createProgram(programForm)
      await createVersion()
    }
    programEditing.value = false
    await load()
  } catch (e: any) {
    formError.value = errorMessage(e, 'Не удалось создать программу.')
  } finally {
    saving.value = false
  }
}

async function createVersion() {
  if (!selectedProgram.value) return
  const version = await trainingApi.createVersion({
    program: selectedProgram.value.id,
    version: Math.max(0, ...selectedProgram.value.versions.map(item => item.version)) + 1,
    ...versionForm,
  })
  selectedProgram.value.versions.unshift(version)
  selectedVersion.value = version
}

let documentRequest = 0
async function loadDocuments(search = '') {
  const request = ++documentRequest
  documentLoading.value = true
  try {
    const data = await DocumentsApi.fetchDocuments({ status: ['active'], page_size: 200, search })
    if (request !== documentRequest) return
    const selectedDocument = documents.value.find(document => document.id === itemForm.document_id)
    const choices = data.results.filter(document => !document.is_dir)
    documents.value = selectedDocument && !choices.some(document => document.id === selectedDocument.id)
      ? [selectedDocument, ...choices]
      : choices
  } catch {
    if (request === documentRequest) formError.value = 'Не удалось загрузить библиотеку документов.'
  } finally {
    if (request === documentRequest) documentLoading.value = false
  }
}
const searchDocuments = useDebounceFn((search: string | null) => loadDocuments(search || ''), 250)

async function openItem(item?: any) {
  if (!selectedVersion.value) return
  formError.value = ''
  Object.assign(itemForm, { item_type: 'text', title: '', description: '', document_id: null, document_version: null, test_version: null, content: '', external_url: '' })
  editingItem.value = item?.id || null
  if (editingItem.value) Object.assign(itemForm, item)
  documentVersions.value = []
  documents.value = item?.document_id ? [{ id: item.document_id, name: item.document_name }] : []
  await loadDocuments()
  if (itemForm.document_id) {
    const edition = itemForm.document_version
    await loadDocumentVersions(itemForm.document_id)
    itemForm.document_version = edition
  }
  itemDialog.value = true
}

async function loadDocumentVersions(documentId: string | null) {
  itemForm.document_version = null
  try {
    documentVersions.value = documentId ? (await DocumentsApi.fetchDocumentDetails(documentId)).versions : []
  } catch {
    formError.value = 'Не удалось загрузить редакции документа.'
  }
}

async function createItem() {
  if (!selectedVersion.value || !itemFormValid.value || saving.value) return
  formError.value = ''
  saving.value = true
  try {
    const payload = {
      version: selectedVersion.value.id,
      item_type: itemForm.item_type as any,
      title: itemForm.title,
      description: itemForm.description,
      order: editingItem.value ? selectedVersion.value.items.find(item => item.id === editingItem.value)?.order || 0 : Math.max(-1, ...selectedVersion.value.items.map(item => item.order)) + 1,
      is_required: true,
      document_version: ['document', 'acknowledgement'].includes(itemForm.item_type) ? itemForm.document_version : null,
      test_version: itemForm.item_type === 'test' ? itemForm.test_version : null,
      content: itemForm.content,
      external_url: itemForm.external_url,
    }
    if (editingItem.value) await trainingApi.updateItem(editingItem.value, payload)
    else await trainingApi.createItem(payload)
    itemDialog.value = false
    await load()
    const refreshed = programs.value.find(item => item.id === selectedProgram.value?.id)
    selectedProgram.value = refreshed || null
    selectedVersion.value = refreshed?.versions.find(item => item.id === selectedVersion.value?.id) || null
  } catch (e: any) {
    formError.value = errorMessage(e, 'Не удалось добавить элемент программы.')
  } finally {
    saving.value = false
  }
}

function openTestBuilder() {
  formError.value = ''
  Object.assign(testForm, { name: '', code: '', passing_percent: 80, time_limit_minutes: 20, max_attempts: 3, questions: [{ text: '', optionsText: '', correctIndex: 0 }] })
  testDialog.value = true
}

function addQuestion() {
  testForm.questions.push({ text: '', optionsText: '', correctIndex: 0 })
}

async function createTest() {
  if (!testFormValid.value || saving.value) return
  formError.value = ''
  saving.value = true
  try {
    const created = await trainingApi.createTest({
      ...testForm,
      questions: testForm.questions.map(question => ({
        text: question.text,
        options: question.optionsText.split('\n').map(value => value.trim()).filter(Boolean),
        correct_answers: [String(question.correctIndex)],
      })),
    })
    tests.value.push(created)
    itemForm.test_version = created.versions[0]?.id || null
    testDialog.value = false
  } catch (e: any) {
    formError.value = errorMessage(e, 'Не удалось создать тест.')
  } finally {
    saving.value = false
  }
}

async function openTakeTest(result: ItemResult) {
  activeTestResult.value = result
  testAnswers.value = {}
  activeAttempt.value = null
  testFeedback.value = ''
  formError.value = ''
  takeTestDialog.value = true
  if (selectedAssignment.value) {
    await runFormAction(async () => {
      activeAttempt.value = (await trainingApi.startTest(selectedAssignment.value!.id, result.item)).id
    }, 'Не удалось начать тест.')
  }
}

async function submitCurrentTest() {
  if (!selectedAssignment.value || !activeTestResult.value || saving.value) return
  saving.value = true
  formError.value = ''
  try {
    if (!activeAttempt.value) activeAttempt.value = (await trainingApi.startTest(selectedAssignment.value.id, activeTestResult.value.item)).id
    const result = await trainingApi.submitTest(activeAttempt.value, testAnswers.value)
    selectedAssignment.value = await trainingApi.getAssignment(selectedAssignment.value.id)
    testFeedback.value = `Результат: ${result.score}%. ${result.passed ? 'Тест пройден.' : 'Тест не пройден. Повторите материал и попробуйте снова, если остались попытки.'}`
    takeTestDialog.value = false
    await load()
  } catch (e: any) {
    formError.value = errorMessage(e, 'Не удалось отправить ответы. Можно повторить отправку.')
  } finally {
    saving.value = false
  }
}

async function publishVersion() {
  if (!selectedVersion.value) return
  await trainingApi.publishVersion(selectedVersion.value.id)
  await load()
  programDialog.value = false
}

function fillCampaign(campaign: TrainingCampaign) {
  Object.assign(campaignForm, { ...campaign, starts_at: toLocalInput(new Date(campaign.starts_at)), due_at: toLocalInput(new Date(campaign.due_at)) })
}
function openCampaign(row?: TrainingCampaign) {
  selectedCampaign.value = row?.id ? row : null
  if (selectedCampaign.value) {
    fillCampaign(selectedCampaign.value)
    formError.value = ''
    campaignDialog.value = true
    return
  }
  formError.value = ''
  const now = new Date()
  const due = new Date(now.getTime() + 7 * 86400000)
  Object.assign(campaignForm, {
    name: '',
    program_version: publishedVersions.value[0]?.value || '',
    starts_at: toLocalInput(now),
    due_at: toLocalInput(due),
    source_type: 'manual',
    source_id: '',
    target_departments: [],
    target_positions: [],
    target_employees: [],
  })
  campaignDialog.value = true
}

async function createCampaign(launch = false) {
  if (!campaignFormValid.value || saving.value) return
  formError.value = ''
  saving.value = true
  try {
    const payload = {
      ...campaignForm,
      starts_at: toIso(campaignForm.starts_at),
      due_at: toIso(campaignForm.due_at),
      source_snapshot: {},
      status: 'draft',
    }
    const created = selectedCampaign.value
      ? await trainingApi.updateCampaign(selectedCampaign.value.id, payload)
      : await trainingApi.createCampaign(payload)
    selectedCampaign.value = created
    if (launch) await trainingApi.launchCampaign(created.id)
    closeCampaign(false)
    await load()
  } catch (e: any) {
    formError.value = errorMessage(e, 'Не удалось создать и запустить кампанию.')
  } finally {
    saving.value = false
  }
}

function closeCampaign(open: boolean) {
  campaignDialog.value = open
  if (!open && route.query.campaign) {
    const query = { ...route.query }
    delete query.campaign
    router.replace({ query })
  }
}
async function completeItem(resultId: number) {
  if (!selectedAssignment.value || saving.value) return
  await runFormAction(async () => {
    selectedAssignment.value = await trainingApi.completeItem(selectedAssignment.value!.id, resultId)
    await load()
  }, 'Не удалось подтвердить изучение.')
}
async function runFormAction(action: () => Promise<unknown>, fallback: string) {
  if (saving.value) return
  saving.value = true
  formError.value = ''
  try {
    await action()
  } catch (e: any) {
    formError.value = errorMessage(e, fallback)
  } finally {
    saving.value = false
  }
}

async function runAction(action: () => Promise<unknown>, fallback: string) {
  error.value = ''
  try {
    await action()
  } catch (e: any) {
    error.value = errorMessage(e, fallback)
  }
}

function exportSelectedAssignment(fileFormat: 'pdf' | 'docx') {
  if (selectedAssignment.value) {
    return runAction(
      () => trainingApi.exportAssignmentDocument(selectedAssignment.value!.id, fileFormat),
      `Не удалось сформировать ${fileFormat.toUpperCase()}.`,
    )
  }
}

onMounted(async () => {
  try {
    // Older registry URLs used the card's campaign key. Preserve those filters
    // without opening a card; standalone campaign links remain unchanged.
    if (['mine', 'matrix'].includes(String(route.query.tab)) && route.query.campaign) {
      const query = { ...route.query, campaign_filter: route.query.campaign_filter || route.query.campaign }
      delete query.campaign
      await router.replace({ query })
    }
    capability.value = await trainingApi.capabilities()
    if (!['mine', ...(capability.value.reports ? ['matrix', 'campaigns'] : []), ...(capability.value.manage ? ['programs', 'campaigns'] : [])].includes(tab.value)) tab.value = 'mine'
    if (capability.value.manage) await Promise.all([staff.fetchDepartments(), staff.fetchPositions(), apiStore.fetchAllEmployees({ silent: true })])
    await load()
    await applyDeepLink()
  } catch (e: any) {
    error.value = errorMessage(e, 'Не удалось загрузить обучение.')
  } finally {
    capabilityLoaded.value = true
  }
})
</script>

<template>
  <VAlert
    v-if="error"
    type="error"
    variant="tonal"
    closable
    class="mb-4"
    @click:close="error = ''"
  >
    {{ error }}<VBtn variant="text" @click="load">
      Повторить
    </VBtn>
  </VAlert>
  <VProgressLinear v-if="!capabilityLoaded" indeterminate />
  <header v-else class="mb-4">
    <VTabs :model-value="tab" show-arrows @update:model-value="switchTab">
      <VTab value="mine">
        Моё обучение
      </VTab>
      <VTab v-if="capability.manage || capability.reports" value="campaigns">
        Кампании
      </VTab>
      <VTab v-if="capability.manage" value="programs">
        Программы
      </VTab>
      <VTab v-if="capability.reports" value="matrix">
        Матрица обученности
      </VTab>
    </VTabs>
  </header>
  <TrainingRegistry
    v-if="capabilityLoaded"
    :key="tab"
    :kind="registryKind"
    :mine="tab === 'mine'"
    :revision="revision"
    @open="row => tab === 'programs' ? openProgram(row) : tab === 'campaigns' ? openCampaign(row) : openAssignment(row)"
  >
    <template #actions>
      <VBtn
        v-if="capability.manage && tab === 'programs'"
        prepend-icon="ri-add-line"
        class="general-button"
        @click="openNewProgram"
      >
        Новая программа
      </VBtn>
      <VBtn
        v-if="capability.manage && tab === 'campaigns'"
        prepend-icon="ri-add-line"
        class="general-button"
        @click="openCampaign"
      >
        Создать кампанию
      </VBtn>
    </template>
  </TrainingRegistry>

  <TrainingDialog
    :model-value="Boolean(selectedAssignment)"
    :title="selectedAssignment?.program_name || 'Обучение'"
    history-kind="assignments"
    :entity-id="selectedAssignment?.id"
    :busy="saving"
    @update:model-value="closeAssignment"
  >
    <template v-if="selectedAssignment">
      <div class="text-medium-emphasis mb-4">
        {{ selectedAssignment.employee_name }} · {{ selectedAssignment.campaign_name }} · до {{ formatDate(selectedAssignment.due_at) }}
      </div><VAlert v-if="formError" type="error" class="mb-3">
        {{ formError }}
      </VAlert><VAlert v-if="testFeedback" type="info" class="mb-3">
        {{ testFeedback }}
      </VAlert>
      <VList lines="three">
        <VListItem
          v-for="result in selectedAssignment.item_results"
          :key="result.id"
          :title="result.title"
          :subtitle="result.passed ? `Завершено ${formatDate(result.completed_at)}` : 'Требуется выполнить'"
          :prepend-icon="result.passed ? 'ri-checkbox-circle-fill' : 'ri-circle-line'"
        >
          <div v-if="result.description" class="text-body-2 mb-2">
            {{ result.description }}
          </div>
          <div v-if="result.content" class="text-body-2 text-medium-emphasis mb-2" style="white-space: pre-line;">
            {{ result.content }}
          </div>
          <div v-if="result.document_name" class="text-caption mb-2">
            Документ: {{ result.document_name }}<span v-if="result.document_edition"> · редакция {{ result.document_edition }}</span>
          </div>
          <div class="d-flex flex-wrap gap-2 mt-3">
            <div class="d-flex flex-column ga-2 align-end">
              <VBtn
                v-if="result.document_url || result.external_url"
                :href="result.document_url || result.external_url"
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                variant="text"
                prepend-icon="ri-external-link-line"
              >
                Открыть материал
              </VBtn>
              <VBtn
                v-if="selectedAssignment.employee === capability.employee && !['cancelled', 'exempt'].includes(selectedAssignment.status) && !result.passed && result.item_type !== 'test'"
                size="small"
                variant="tonal"
                :loading="saving"
                @click="completeItem(result.id)"
              >
                Подтвердить изучение
              </VBtn><VBtn
                v-else-if="selectedAssignment.employee === capability.employee && !['cancelled', 'exempt'].includes(selectedAssignment.status) && result.item_type === 'test' && !result.passed"
                color="warning"
                size="small"
                variant="tonal"
                @click="openTakeTest(result)"
              >
                Пройти тест
              </VBtn>
            </div>
          </div>
        </VListItem>
      </VList>
    </template>
    <template #actions>
      <VBtn variant="outlined" prepend-icon="ri-printer-line" @click="runAction(() => trainingApi.printAssignment(selectedAssignment!.id), 'Не удалось открыть печать.')">
        Печать
      </VBtn><VBtn variant="outlined" @click="exportSelectedAssignment('pdf')">
        PDF
      </VBtn><VBtn variant="outlined" @click="exportSelectedAssignment('docx')">
        Word
      </VBtn>
    </template>
  </TrainingDialog>

  <TrainingDialog
    :model-value="programDialog"
    :title="selectedProgram?.name || 'Новая программа'"
    history-kind="programs"
    :entity-id="selectedProgram?.id"
    :snapshot="programEditing ? JSON.stringify(programForm) : ''"
    :busy="saving"
    @update:model-value="closeProgram"
  >
    <VAlert
      v-if="formError"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ formError }}
    </VAlert>
    <template v-if="programEditing">
      <div class="form-stack">
        <VTextField v-model="programForm.name" label="Название" required /><VTextField
          v-model="programForm.code"
          label="Код"
          hint="Латиницей, например patient-identification"
          persistent-hint
          required
        /><VTextField v-model="programForm.category" label="Категория" /><VTextarea v-model="programForm.description" label="Цель и описание" />
      </div>
    </template>
    <template v-else>
      <VBtn
        class="mb-3"
        variant="text"
        prepend-icon="ri-edit-line"
        @click="programEditing = true"
      >
        Редактировать описание
      </VBtn><VSelect
        v-model="selectedVersion"
        :items="selectedProgram?.versions || []"
        item-title="version"
        return-object
        label="Версия программы"
      /><VAlert v-if="!selectedVersion" type="info" variant="tonal">
        Создайте первую версию программы.
      </VAlert><template v-else>
        <div class="d-flex justify-space-between align-center mb-3">
          <div><b>Версия {{ selectedVersion.version }}</b> · {{ statusLabel[selectedVersion.status] || selectedVersion.status }}</div><VBtn
            v-if="selectedVersion.status === 'draft'"
            size="small"
            prepend-icon="ri-add-line"
            @click="openItem"
          >
            Добавить материал
          </VBtn>
        </div><VList border rounded>
          <VListItem
            v-for="item in selectedVersion.items"
            :key="item.id"
            :title="item.title"
            :subtitle="item.document_name ? `${item.document_name}, редакция ${item.document_edition}` : item.description"
            prepend-icon="ri-file-list-3-line"
            :append-icon="selectedVersion.status === 'draft' ? 'ri-edit-line' : undefined"
            @click="selectedVersion.status === 'draft' && openItem(item)"
          /><VListItem v-if="!selectedVersion.items.length" title="Элементы ещё не добавлены" />
        </VList>
      </template>
    </template>

    <template #actions>
      <VBtn
        v-if="programEditing"
        :loading="saving"
        :disabled="!programForm.name.trim() || !programForm.code.trim()"
        @click="createProgram"
      >
        Сохранить
      </VBtn><VBtn v-else-if="!selectedVersion" @click="runFormAction(createVersion, 'Не удалось создать версию.')">
        Создать версию
      </VBtn><VBtn v-else-if="selectedVersion.status === 'draft'" color="success" @click="runFormAction(publishVersion, 'Не удалось опубликовать программу.')">
        Опубликовать
      </VBtn><VBtn v-else prepend-icon="ri-add-line" @click="runFormAction(createVersion, 'Не удалось создать версию.')">
        Новая версия
      </VBtn>
    </template>
  </TrainingDialog>

  <TrainingDialog
    v-model="itemDialog"
    :title="editingItem ? 'Редактирование материала' : 'Элемент программы'"
    nested
    :snapshot="JSON.stringify(itemForm)"
    :busy="saving"
  >
    <VAlert
      v-if="formError"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ formError }}
    </VAlert>
    <div class="form-stack">
      <VSelect v-model="itemForm.item_type" :items="[{ title: 'Материал', value: 'text' }, { title: 'Документ с ознакомлением', value: 'acknowledgement' }, { title: 'Тест', value: 'test' }, { title: 'Ссылка', value: 'link' }]" label="Тип" /><VTextField v-model="itemForm.title" label="Название" required /><VTextarea v-model="itemForm.description" label="Пояснение сотруднику" /><template v-if="['document', 'acknowledgement'].includes(itemForm.item_type)">
        <VAutocomplete
          v-model="itemForm.document_id"
          :items="documents"
          :loading="documentLoading"
          item-title="name"
          item-value="id"
          label="Документ"
          hint="Введите название для поиска по библиотеке"
          persistent-hint
          no-filter
          @update:search="searchDocuments"
          @update:model-value="loadDocumentVersions"
        /><VSelect
          v-model="itemForm.document_version"
          :items="documentVersions"
          :item-title="item => `Редакция ${item.edition || item.version}`"
          item-value="id"
          label="Точная редакция"
        />
      </template><VTextarea v-else-if="itemForm.item_type === 'text'" v-model="itemForm.content" label="Содержание" /><div v-else-if="itemForm.item_type === 'test'" class="d-flex align-center gap-2">
        <VSelect v-model="itemForm.test_version" :items="testVersionItems" label="Опубликованный тест" /><VBtn
          icon="ri-add-line"
          variant="tonal"
          title="Создать тест"
          @click="openTestBuilder"
        />
      </div><VTextField
        v-else
        v-model="itemForm.external_url"
        type="url"
        label="Ссылка"
      />
    </div>

    <template #actions>
      <VBtn :loading="saving" :disabled="!itemFormValid" @click="createItem">
        {{ editingItem ? 'Сохранить' : 'Добавить' }}
      </VBtn>
    </template>
  </TrainingDialog>

  <TrainingDialog
    v-model="testDialog"
    title="Новый тест знаний"
    nested
    :snapshot="JSON.stringify(testForm)"
    :busy="saving"
  >
    <VAlert
      v-if="formError"
      type="error"
      variant="tonal"
      class="mb-4"
    >
      {{ formError }}
    </VAlert>
    <VTextField v-model="testForm.name" label="Название" />
    <VTextField v-model="testForm.code" label="Код теста" />
    <VRow>
      <VCol cols="12" sm="4">
        <VTextField
          v-model.number="testForm.passing_percent"
          type="number"
          min="1"
          max="100"
          label="Проходной балл, %"
        />
      </VCol>
      <VCol cols="12" sm="4">
        <VTextField
          v-model.number="testForm.max_attempts"
          type="number"
          min="1"
          label="Попыток"
        />
      </VCol>
      <VCol cols="12" sm="4">
        <VTextField
          v-model.number="testForm.time_limit_minutes"
          type="number"
          min="1"
          label="Минут"
        />
      </VCol>
    </VRow>
    <VCard
      v-for="(question, index) in testForm.questions"
      :key="index"
      variant="outlined"
      class="pa-4 mb-3"
    >
      <div class="font-weight-bold mb-2">
        Вопрос {{ index + 1 }}
      </div>
      <VTextarea v-model="question.text" label="Текст вопроса" rows="2" />
      <VTextarea v-model="question.optionsText" label="Варианты ответа — каждый с новой строки" rows="3" />
      <VSelect v-model="question.correctIndex" :items="question.optionsText.split('\n').map(value => value.trim()).filter(Boolean).map((title, value) => ({ title, value }))" label="Правильный ответ" />
    </VCard>
    <VBtn variant="tonal" prepend-icon="ri-add-line" @click="addQuestion">
      Добавить вопрос
    </VBtn>

    <template #actions>
      <VBtn :loading="saving" :disabled="!testFormValid" @click="createTest">
        Создать тест
      </VBtn>
    </template>
  </TrainingDialog>

  <TrainingDialog
    v-model="takeTestDialog"
    :title="activeTestResult?.test?.name || 'Проверка знаний'"
    nested
    :snapshot="JSON.stringify(testAnswers)"
    :busy="saving"
  >
    <template v-if="activeTestResult?.test">
      <VAlert v-if="formError" type="error" class="mb-3">
        {{ formError }}
      </VAlert><p class="mb-4">
        Проходной балл: {{ activeTestResult.test.passing_percent }}%. Попыток: {{ activeTestResult.test.max_attempts }}. Время на попытку: {{ activeTestResult.test.time_limit_minutes }} мин.
      </p>
      <div v-for="(question, index) in activeTestResult.test.questions" :key="question.id" class="mb-5">
        <div class="font-weight-bold mb-2">
          {{ index + 1 }}. {{ question.text }}
        </div>
        <VRadioGroup v-model="testAnswers[question.id]" inline>
          <VRadio
            v-for="(option, optionIndex) in question.options"
            :key="optionIndex"
            :label="option"
            :value="[String(optionIndex)]"
          />
        </VRadioGroup>
      </div>
    </template>
    <template #actions>
      <VBtn :loading="saving" :disabled="!currentTestComplete || !activeAttempt" @click="submitCurrentTest">
        Завершить тест
      </VBtn>
    </template>
  </TrainingDialog>

  <TrainingDialog
    :model-value="campaignDialog"
    :title="selectedCampaign?.name || 'Новая учебная кампания'"
    history-kind="campaigns"
    :entity-id="selectedCampaign?.id"
    :snapshot="!selectedCampaign || ['draft', 'scheduled'].includes(selectedCampaign.status) ? JSON.stringify(campaignForm) : ''"
    :busy="saving"
    @update:model-value="closeCampaign"
  >
    <template v-if="capability.manage && (!selectedCampaign || ['draft', 'scheduled'].includes(selectedCampaign.status))">
      <VAlert
        v-if="formError"
        type="error"
        variant="tonal"
        class="mb-4"
      >
        {{ formError }}
      </VAlert>
      <div class="form-stack">
        <VTextField v-model="campaignForm.name" label="Название кампании" required /><VSelect
          v-model="campaignForm.program_version"
          :items="publishedVersions"
          label="Программа"
          required
        /><VRow>
          <VCol cols="12" sm="6">
            <VTextField v-model="campaignForm.starts_at" type="datetime-local" label="Начало" />
          </VCol><VCol cols="12" sm="6">
            <VTextField v-model="campaignForm.due_at" type="datetime-local" label="Срок" />
          </VCol>
        </VRow><VSelect v-model="campaignForm.source_type" :items="[{ title: 'Ручное назначение', value: 'manual' }, { title: 'Новая версия документа', value: 'document' }, { title: 'Аудит', value: 'audit' }, { title: 'Нежелательное событие', value: 'adverse' }, { title: 'Улучшение', value: 'improvement' }]" label="Основание" /><VTextField v-if="campaignForm.source_type !== 'manual'" v-model="campaignForm.source_id" label="Номер или идентификатор основания" /><VSelect
          v-model="campaignForm.target_departments"
          :items="departmentItems"
          multiple
          chips
          label="Подразделения"
        /><VSelect
          v-model="campaignForm.target_positions"
          :items="staff.positionsList"
          item-title="name"
          item-value="id"
          multiple
          chips
          label="Должности"
        />
        <div>
          <VBtn variant="outlined" prepend-icon="ri-user-add-line" @click="employeeSelector?.open()">
            Выбрать сотрудников
          </VBtn>
          <div v-if="selectedEmployees.length" class="d-flex flex-wrap gap-2 mt-2">
            <VChip
              v-for="employee in selectedEmployees"
              :key="employee.id"
              closable
              @click:close="campaignForm.target_employees = campaignForm.target_employees.filter(id => id !== employee.id)"
            >
              {{ employee.full_name }}
            </VChip>
          </div>
        </div>
        <VAlert v-if="campaignForm.starts_at && campaignForm.due_at && new Date(campaignForm.due_at) <= new Date(campaignForm.starts_at)" type="warning" variant="tonal">
          Срок завершения должен быть позже даты начала.
        </VAlert><VAlert type="info" variant="tonal">
          Сохраните черновик или запустите кампанию. При запуске сотрудникам поступят уведомления.
        </VAlert>
      </div>
    </template><template v-else-if="selectedCampaign">
      <p class="text-h6">
        {{ selectedCampaign.program_name }}
      </p><VChip :color="statusColor[selectedCampaign.status]">
        {{ statusLabel[selectedCampaign.status] }}
      </VChip><p class="mt-4">
        Назначено: {{ selectedCampaign.assignment_count }} · Завершено: {{ selectedCampaign.completed_count }} · Просрочено: {{ selectedCampaign.overdue_count }}
      </p><p>Срок: {{ formatDate(selectedCampaign.due_at) }}</p>
    </template>
    <template #actions>
      <template v-if="capability.manage && (!selectedCampaign || ['draft', 'scheduled'].includes(selectedCampaign.status))">
        <VBtn
          variant="outlined"
          :disabled="!campaignFormValid"
          :loading="saving"
          @click="createCampaign(false)"
        >
          Сохранить черновик
        </VBtn><VBtn :disabled="!campaignFormValid" :loading="saving" @click="createCampaign(true)">
          Запустить обучение
        </VBtn>
      </template><template v-else-if="selectedCampaign">
        <VBtn variant="outlined" @click="runAction(() => trainingApi.exportCampaign(selectedCampaign!.id), 'Не удалось выгрузить Excel.')">
          Excel
        </VBtn><VBtn variant="outlined" @click="runAction(() => trainingApi.printCampaign(selectedCampaign!.id), 'Не удалось открыть печать.')">
          Печать
        </VBtn><VBtn variant="outlined" @click="runAction(() => trainingApi.exportCampaignDocument(selectedCampaign!.id, 'pdf'), 'Не удалось сформировать PDF.')">
          PDF
        </VBtn><VBtn variant="outlined" @click="runAction(() => trainingApi.exportCampaignDocument(selectedCampaign!.id, 'docx'), 'Не удалось сформировать Word.')">
          Word
        </VBtn>
      </template>
    </template>
  </TrainingDialog>
  <EmployeeSelectionModal
    ref="employeeSelector"
    :employees="apiStore.employees"
    :selected-employees="selectedEmployees"
    multiple
    title="Сотрудники для обучения"
    @select-multiple="employees => campaignForm.target_employees = employees.map(employee => employee.id)"
  />
</template>

<style scoped>
.form-stack { display: flex; flex-direction: column; gap: 16px; }
:deep(.v-list-item-title) { white-space: normal; overflow-wrap: anywhere; }
</style>
